import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LocalStorage } from 'angular-web-storage';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import {
  CustomerBooking,
  CustomerBookingParticipant
} from '../../../../../../../models/customer';
import { ListResponse } from '../../../../../../../models/list-response';
import { AccountService } from '../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { dateStringFromISO8601 } from '../../../../../../../utils';
import { getBookingEventDetailsLink } from '../../../../../../../models/report-columns/bookings';
import { DetailsModalDeepLink } from '../../../../../details-modal-deep-link';
import {
  BOOKING_DETAILS_QUERY_PARAM,
  BookingDetailsModalComponent
} from '../../../../events/modals/booking-details-modal/booking-details-modal.component';

/**
 * The "Bookings" view of a customer's Reservations tab (PRO-1338): one row per
 * event booking the customer made or is a participant in, however many
 * participants it holds.
 */
@Component({
  selector: 'app-customer-bookings-sub-list',
  templateUrl: './customer-bookings-sub-list.component.html',
  styleUrls: ['./customer-bookings-sub-list.component.css']
})
export class CustomerBookingsSubListComponent implements OnInit, OnDestroy {

  public pageNumber = 1;
  @LocalStorage() public pageSizeCustomerBookings;

  public filtersChangedSubject = new Subject<void>();

  public customerId: number;

  public bookingsList: ListResponse<CustomerBooking>;
  public loadingBookings = false;

  // Bookings a rolling membership's roster generated on the customer's behalf.
  // Hidden by default: they arrive one per occurrence and are not something
  // staff pay or modify.
  public showRosterBookings = false;

  // Bookings auto-cancelled because they were never paid for. Hidden by
  // default so an abandoned checkout does not read as an outstanding booking.
  public showNonpaymentCancelledBookings = false;

  public customerNotFound = false;
  public serverError: number = null;

  // Make relevant library functions available to the template.
  public dateStringFromISO8601 = dateStringFromISO8601;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private listRequestCount = 0;

  private customerParamsSubscription: Subscription;

  private bookingDetailsDeepLink: DetailsModalDeepLink<BookingDetailsModalComponent>;

  constructor(
    public accountService: AccountService,
    public organizationConfigService: OrganizationConfigService,
    private route: ActivatedRoute,
    private customerService: CustomerService,
    router: Router,
    modalService: NgbModal
  ) {
    this.bookingDetailsDeepLink = new DetailsModalDeepLink<BookingDetailsModalComponent>({
      router,
      route,
      modalService,
      queryParam: BOOKING_DETAILS_QUERY_PARAM,
      component: BookingDetailsModalComponent,
      configure: (instance, bookingId) => { instance.bookingId = bookingId; }
    });
  }

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    if (!this.pageSizeCustomerBookings) {
      this.pageSizeCustomerBookings = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeCustomerBookings = this.pageSizeCustomerBookings;

    // Handle changes to the filters by reloading the list, subject to debounce.
    this.filtersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadBookings();
    });

    // The customer ID is on the grandparent (customer-detail) route.
    // Subscribed, not snapshotted: when only :id changes Angular reuses this
    // component without re-running ngOnInit.
    this.customerParamsSubscription =
      this.route.parent.parent.params.subscribe(routeParams => {
        this.customerId = parseInt(routeParams.id, 10);
        if (isNaN(this.customerId)) {
          this.customerNotFound = true;
          return;
        }
        this.customerNotFound = false;
        // Both gate the whole view; a stale error would cover the next
        // customer's data.
        this.serverError = null;
        this.pageNumber = 1;
        this.loadBookings();
      });

    this.bookingDetailsDeepLink.start();
  }

  ngOnDestroy() {
    if (this.customerParamsSubscription) {
      this.customerParamsSubscription.unsubscribe();
    }
    this.bookingDetailsDeepLink.stop();
  }

  public openBookingDetailsModal(booking: CustomerBooking) {
    this.bookingDetailsDeepLink.open(booking.id);
  }

  /**
   * Reload the list when one of the display toggles changes, resetting to the
   * first page.
   */
  public onFiltersChange() {
    this.pageNumber = 1;
    this.filtersChangedSubject.next();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.bookingsList) { return 1; }
    return Math.ceil(this.bookingsList.count / this.pageSizeCustomerBookings);
  }

  /**
   * The Staff Site path to the booking's event details page, or null.
   * Delegates to the shared report-column helper so this view and the
   * Bookings report agree on when an event is linkable.
   */
  public getBookingEventDetailsLink(booking: CustomerBooking): string | null {
    return getBookingEventDetailsLink({
      event_first_part_id: booking.event_first_part_id,
      event_start_datetime: booking.event_start_datetime,
      event_end_datetime: booking.event_end_datetime,
      event_start_date: booking.event_start_local_date,
    });
  }

  /**
   * Whether every participant on the booking is named in `participants`. The
   * API caps that list, so a booking with more participants than the cap shows
   * a count instead of a list of names.
   */
  public allParticipantsAreNamed(booking: CustomerBooking): boolean {
    return booking.participants.length >= booking.num_participants;
  }

  /**
   * The name to show for a participant not linked to a customer, or null. A
   * private event request's placeholder reservation has both name fields
   * empty, which must not render as an empty cell.
   */
  public getUnlinkedParticipantName(participant: CustomerBookingParticipant): string | null {
    const name = (participant.first_name + ' ' + participant.last_name).trim();

    return name === '' ? null : name;
  }

  /**
   * Whether the booking has an amount still owing.
   */
  public hasAmountDue(booking: CustomerBooking): boolean {
    return booking.invoice_amount_due > 0;
  }


  private loadBookings() {
    this.loadingBookings = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.customerService
      .getCustomerBookings(
        this.customerId,
        this.pageNumber,
        this.pageSizeCustomerBookings,
        this.showRosterBookings,
        this.showNonpaymentCancelledBookings
      )
      .then(results => {
        // Only update the list if there has not been a more recent request
        // initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.bookingsList = results;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingBookings = false;
        }
      });
  }

}
